import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import Corners from "./Corners";

const PORTRAIT =
  "https://customer-assets-lqy194kg.emergentagent.net/job_webinar-funnel-mlo/artifacts/gtdebat3_574A6668-Enhanced-NR.webp";

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [-30, 60]);

  return (
    <section
      ref={ref}
      className="relative py-32 md:py-48 border-t wam-hairline"
      data-testid="about-section"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
          className="mb-16 flex items-center gap-4"
        >
          <span className="font-mono text-[11px] tracking-[0.3em] text-[#F0ECE4]/40">
            [007] —
          </span>
          <span className="font-mono text-[11px] tracking-[0.3em] text-[#C9A84C]">
            WHO BUILT THIS
          </span>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_1.2fr] gap-10 md:gap-20 items-start">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 1 }}
            className="relative"
          >
            <div className="relative aspect-[4/5] w-full overflow-hidden border border-[#C9A84C]/30 bg-[#F0ECE4]">
              <Corners />
              <motion.img
                src={PORTRAIT}
                alt="Dallas Bell"
                className="h-full w-full object-cover"
                style={{ y: imgY, objectPosition: "50% 35%", filter: "contrast(1.02) saturate(1.05)" }}
                data-testid="dallas-portrait"
              />
              {/* Warm gold light-leak at bottom to tie into brand */}
              <div
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(to top, rgba(201,168,76,0.14) 0%, transparent 30%), linear-gradient(to bottom, rgba(7,7,9,0.08) 0%, transparent 20%)",
                  mixBlendMode: "multiply",
                }}
              />
              {/* Grain overlay to unify with page texture */}
              <div
                aria-hidden
                className="absolute inset-0 pointer-events-none opacity-[0.12] mix-blend-multiply"
                style={{
                  backgroundImage:
                    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
                }}
              />
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between font-mono text-[9px] tracking-[0.3em] text-[#070709]/70">
                <span>DALLAS BELL</span>
                <span>SBA / 2025</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 1, delay: 0.15 }}
          >
            <h2
              className="font-display"
              style={{ fontSize: "clamp(32px, 4.5vw, 64px)", lineHeight: 0.95, letterSpacing: "-0.035em" }}
            >
              Built by someone who
              <br />
              <span className="text-[#C9A84C]">runs the ads himself.</span>
            </h2>

            <p className="mt-8 font-body text-[15px] leading-[1.8] text-[#F0ECE4]/75 max-w-[560px]">
              Dallas Bell is the founder of Small Business Automator and the
              architect of the WAM system. He doesn&rsquo;t sell a course — he runs the
              same Meta ad funnels, the same GHL automations, and the same webinar
              model for his own clients. When you buy WAM, you&rsquo;re getting the
              exact system that produced Cassandra&rsquo;s 221 leads in 10 days.
            </p>

            <div className="mt-10 flex flex-wrap gap-x-4 gap-y-3 font-mono text-[10px] tracking-[0.28em] text-[#C9A84C]/85">
              <span>FOUNDER</span>
              <span className="text-[#F0ECE4]/25">·</span>
              <span>SMALL BUSINESS AUTOMATOR</span>
              <span className="text-[#F0ECE4]/25">·</span>
              <span>META ADS SPECIALIST</span>
              <span className="text-[#F0ECE4]/25">·</span>
              <span>GHL CERTIFIED PARTNER</span>
            </div>

            <button
              onClick={() => document.getElementById("apply")?.scrollIntoView({ behavior: "smooth" })}
              className="mt-12 group inline-flex items-center gap-3 font-mono text-[11px] tracking-[0.3em] text-[#C9A84C]"
              data-testid="about-cta"
            >
              <span className="border-b border-[#C9A84C]/40 pb-1 transition-colors group-hover:border-[#C9A84C]">
                WORK WITH DALLAS
              </span>
              <span className="transition-transform group-hover:translate-x-1">→</span>
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
